'use client'

import * as React from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cn } from '../utils/cn'
import { IconChevronDown } from '@tabler/icons-react'

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
))
AccordionItem.displayName = 'AccordionItem'

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    headerClassName?: string
    withIcon?: boolean
    chevronIconClassName?: string
  }
>(
  (
    {
      className,
      children,
      headerClassName,
      withIcon = true,
      chevronIconClassName,
      ...props
    },
    ref,
  ) => {
    if (props.asChild)
      return (
        <AccordionPrimitive.Header className={cn('flex', headerClassName)}>
          <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
              'flex flex-1 items-center justify-between py-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180',
              className,
            )}
            {...props}
          >
            {children}
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
      )

    return (
      <AccordionPrimitive.Header className='flex'>
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex flex-1 items-center justify-between py-4 font-medium transition-all [&[data-state=open]>svg]:rotate-180',
            className,
          )}
          {...props}
        >
          {children}
          {withIcon && (
            <IconChevronDown
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200',
                chevronIconClassName,
              )}
            />
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    )
  },
)
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    rootClassName?: string
    clipClassName?: string
  }
>(
  (
    { className, rootClassName, clipClassName, children, forceMount, ...props },
    ref,
  ) => {
    if (forceMount)
      return (
        <AccordionPrimitive.Content
          ref={ref}
          forceMount={forceMount}
          //Force-mounted content stays in the DOM while collapsed, so open and
          //closed states are driven by grid rows instead of the Radix
          //height-var animations (which never hide force-mounted content and
          //flash it open on first paint). The duration must be !important:
          //radix sets an inline transition-duration of 0s while it measures
          //the node on every toggle, which otherwise commits the new grid
          //rows without ever starting the transition
          className={cn(
            'text-sm grid transition-[grid-template-rows,visibility] !duration-200 data-[state=open]:grid-rows-[1fr] data-[state=closed]:grid-rows-[0fr] data-[state=closed]:invisible',
            rootClassName,
          )}
          {...props}
        >
          {/*The clipping wrapper carries no padding so the 0fr track can
          collapse it to exactly zero height; the padded layer lives inside
          and gets clipped with the rest of the content*/}
          <div className={cn('min-h-0 overflow-hidden', clipClassName)}>
            <div className={cn('pb-4 pt-0', className)}>{children}</div>
          </div>
        </AccordionPrimitive.Content>
      )

    return (
      <AccordionPrimitive.Content
        ref={ref}
        className={cn(
          'overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
          rootClassName,
        )}
        {...props}
      >
        <div className={cn('pb-4 pt-0', className)}>{children}</div>
      </AccordionPrimitive.Content>
    )
  },
)
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
